import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gastosAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useZonaHoraria } from '../context/ZonaHorariaContext';
import { loadFiltrosSession, saveFiltrosSession } from '../utils/filtrosSession';
import '../Clientes.css';

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CLAVE_FILTROS = 'gastos_listado_filtros';
const defaultFiltrosGastos = { fecha_desde: '', fecha_hasta: '' };

const GastosList = () => {
  const { hasPermission } = useAuth();
  const { formatFecha, fechaHoy } = useZonaHoraria();
  const [gastos, setGastos] = useState([]);
  const [loading, setLoading] = useState(true);
  const primeraCargaRef = useRef(true);
  const [refrescando, setRefrescando] = useState(false);
  const cargaSeqRef = useRef(0);
  const [filtros, setFiltros] = useState(() => loadFiltrosSession(CLAVE_FILTROS, defaultFiltrosGastos));
  const fechaDefaultAplicadaRef = useRef(false);

  useEffect(() => {
    saveFiltrosSession(CLAVE_FILTROS, filtros);
  }, [filtros]);

  useEffect(() => {
    if (fechaDefaultAplicadaRef.current) return;
    fechaDefaultAplicadaRef.current = true;
    setFiltros((prev) => (prev.fecha_desde ? prev : { ...prev, fecha_desde: fechaHoy() }));
  }, [fechaHoy]);

  const loadGastos = useCallback(async () => {
    const mostrarLoaderPantalla = primeraCargaRef.current;
    const seq = ++cargaSeqRef.current;
    try {
      if (mostrarLoaderPantalla) setLoading(true);
      else setRefrescando(true);
      const params = {};
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const res = await gastosAPI.getAll(params);
      if (seq !== cargaSeqRef.current) return;
      if (res.success) {
        setGastos(Array.isArray(res.data) ? res.data : []);
      } else {
        setGastos([]);
      }
    } catch (e) {
      if (seq !== cargaSeqRef.current) return;
      setGastos([]);
      toast.error(e.message || 'Error al cargar gastos');
    } finally {
      if (mostrarLoaderPantalla) {
        setLoading(false);
        primeraCargaRef.current = false;
      }
      if (seq === cargaSeqRef.current) setRefrescando(false);
    }
  }, [filtros.fecha_desde, filtros.fecha_hasta]);

  useEffect(() => {
    loadGastos();
  }, [loadGastos]);

  const handleAnular = async (id, concepto) => {
    if (!hasPermission('anular_gastos')) {
      toast.error('No tiene permiso para anular gastos');
      return;
    }
    if (!window.confirm(`¿Anular el gasto "${concepto}"? Se revertirán los movimientos de fondos asociados.`)) return;
    try {
      const res = await gastosAPI.anular(id);
      if (res.success) {
        toast.success(res.message || 'Gasto anulado');
        loadGastos();
      } else {
        toast.error(res.message || 'No se pudo anular');
      }
    } catch (e) {
      toast.error(e.message || 'Error al anular gasto');
    }
  };

  const totalFiltrado = gastos.reduce((s, g) => s + (parseFloat(g.monto) || 0), 0);

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>Gastos</h1>
        {hasPermission('crear_gastos') && (
          <Link to="/gastos/nuevo" className="btn-primary">+ Nuevo gasto</Link>
        )}
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="filters" style={{ flexWrap: 'wrap', gap: '8px 12px', alignItems: 'center' }}>
          <input
            type="date"
            value={filtros.fecha_desde}
            onChange={(e) => setFiltros((p) => ({ ...p, fecha_desde: e.target.value }))}
            title="Fecha desde"
          />
          <input
            type="date"
            value={filtros.fecha_hasta}
            onChange={(e) => setFiltros((p) => ({ ...p, fecha_hasta: e.target.value }))}
            title="Fecha hasta"
          />
          <button type="button" className="btn-secondary" onClick={() => setFiltros({ ...defaultFiltrosGastos })}>
            Limpiar fechas
          </button>
        </div>

        {gastos.length > 0 && (
          <div style={{ marginBottom: 0, padding: 12, background: '#f0f4f8', borderRadius: 8 }}>
            <strong>Total del listado:</strong> ${formatMoneda(totalFiltrado)}
          </div>
        )}

        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className={`listado-tabla-area clientes-listado-tabla-area${refrescando ? ' listado-tabla-area--refreshing clientes-listado-tabla-area--refreshing' : ''}`}>
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Concepto</th>
              <th>Tipo</th>
              <th>Proveedor</th>
              <th>Monto</th>
              <th>Usuario</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="no-data">Cargando...</td></tr>
            ) : gastos.length === 0 ? (
              <tr><td colSpan="7" className="no-data">No hay gastos en el período</td></tr>
            ) : (
              gastos.map((g) => (
                <tr key={g.id}>
                  <td>{formatFecha(g.fecha)}</td>
                  <td>{g.concepto}</td>
                  <td>{g.tipo_nombre || '-'}</td>
                  <td>{g.proveedor_nombre || '-'}</td>
                  <td><strong>${formatMoneda(g.monto)}</strong></td>
                  <td>{g.usuario_nombre || '-'}</td>
                  <td>
                    {hasPermission('anular_gastos') && (
                      <button type="button" className="btn-delete" onClick={() => handleAnular(g.id, g.concepto)}>
                        Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
            </div>
          {refrescando && (
            <div className="listado-refresh-overlay clientes-listado-refresh-overlay" role="status" aria-live="polite">
              <span className="clientes-listado-refresh-spinner" aria-hidden="true" />
              <span className="clientes-listado-refresh-label">Actualizando…</span>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GastosList;
